import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type AgentDashboardStateWire } from '../lib/api-client';

const AGENT_DASHBOARD_KEY = ['agent-dashboard', 'state'] as const;
const LAYOUT_NAMESPACE = 'agent-dashboard-layout';

/**
 * useAgentDashboard — estado do Painel Pessoal (Brief 2B B5,
 * `GET /v1/agent-dashboard/state`). Composição read-time, caller-scoped:
 * o backend já agrega os 6 blocos reais + 7 honest-defer (null) + layout.
 */
export function useAgentDashboard(): ReturnType<
  typeof useQuery<AgentDashboardStateWire, Error>
> {
  return useQuery({
    queryKey: AGENT_DASHBOARD_KEY,
    queryFn: () => api.getAgentDashboardState(),
    staleTime: 30_000,
  });
}

/** Layout P3.6 a persistir (keys visible/order no namespace agent-dashboard-layout). */
interface UpdateLayoutVars {
  visible: string[];
  order: string[];
}

/**
 * useUpdateDashboardLayout — persiste o layout P3.6 server-side (UserPreference
 * namespace 'agent-dashboard-layout', keys 'visible'/'order') e invalida o painel.
 *
 * Requer a capability `user-preferences-manage`; sem ela o PUT 403 — a página
 * trata com toast honesto e segue read-only.
 */
export function useUpdateDashboardLayout(): ReturnType<
  typeof useMutation<void, Error, UpdateLayoutVars>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ visible, order }: UpdateLayoutVars) => {
      await api.setUserPreference(LAYOUT_NAMESPACE, 'visible', visible);
      await api.setUserPreference(LAYOUT_NAMESPACE, 'order', order);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENT_DASHBOARD_KEY });
    },
  });
}
